import { X } from 'lucide-react';
import { useCallback, useState } from 'react';

import {
    ConversationRecipientHistory,
    useConversationRecipientPreview,
} from '@apps/web/components/shared/messages/conversation_recipient_history';
import {
    type ConversationRecipient,
    ConversationRecipientPicker,
} from '@apps/web/components/shared/messages/conversation_recipient_picker';
import { createConversationMessage } from '@apps/web/components/shared/messages/create_conversation_message';
import { MessageComposer } from '@apps/web/components/shared/messages/message_composer';
import { Button } from '@apps/web/components/ui/button';
import { useComposerFooterHeight } from '@apps/web/hooks/use_composer_footer_height';
import { toast } from '@apps/web/lib/app_notifications';
import { ApiError } from '@libraries/typescript/api_client';

type ConversationDraftPaneProps = {
    readonly organizationId: string;
    readonly timezone: string;
    // Preselects who the thread is with, e.g. an Inbox call's caller.
    readonly initialRecipient?: ConversationRecipient | null;
    // Omit to hide the discard button when the host owns closing.
    readonly onCancel?: () => void;
    readonly onMessageCreated: (conversationId: string) => void | Promise<void>;
};

/**
 * The inbox's new-message thread: a conversation that has no messages and no
 * id yet. It is deliberately the same three parts as a real thread -- a
 * header naming who it is with, an empty body, and the real MessageComposer
 * -- so that sending the first message changes what is on screen as little as
 * possible.
 */
export function ConversationDraftPane({
    organizationId,
    timezone,
    initialRecipient = null,
    onCancel,
    onMessageCreated,
}: ConversationDraftPaneProps) {
    const setComposerFooterNode = useComposerFooterHeight();
    const [search, setSearch] = useState('');
    const [recipient, setRecipient] = useState<ConversationRecipient | null>(
        initialRecipient,
    );
    const preview = useConversationRecipientPreview(organizationId, recipient);
    const [composerBusy, setComposerBusy] = useState(false);
    const handleBusyChange = useCallback((busy: boolean) => {
        setComposerBusy(busy);
    }, []);
    const draftRecipientKey =
        recipient === null
            ? ''
            : recipient.kind === 'caregiver'
              ? `caregiver:${recipient.caregiver.id}`
              : recipient.kind === 'client'
                ? 'client:' +
                  recipient.client.id +
                  ':' +
                  recipient.client.recipientKey +
                  ':' +
                  recipient.client.phoneNumber
                : `phone:${recipient.phoneNumber}`;

    return (
        <div className="flex h-full min-h-0 flex-col">
            <ConversationRecipientPicker
                disabled={composerBusy}
                onRecipientChange={setRecipient}
                onSearchChange={setSearch}
                organizationId={organizationId}
                recipient={recipient}
                search={search}
                searchReason="conversation-draft-pane"
                trailing={
                    onCancel ? (
                        <Button
                            aria-label="Discard draft"
                            onClick={onCancel}
                            size="icon"
                            type="button"
                            variant="ghost"
                        >
                            <X className="size-4" />
                        </Button>
                    ) : undefined
                }
                variant="header"
            />
            <ConversationRecipientHistory
                organizationId={organizationId}
                timezone={timezone}
                preview={preview}
            />
            <div
                ref={setComposerFooterNode}
                className="bg-background sticky bottom-0 z-10 flex-shrink-0 border-t pt-3 pb-[calc(1rem+env(safe-area-inset-bottom))] md:static md:border-t-0 md:py-4"
            >
                <div className="w-full px-4">
                    <MessageComposer
                        conversationId=""
                        draft={{
                            caregiverId:
                                recipient?.kind === 'caregiver'
                                    ? recipient.caregiver.id
                                    : null,
                            recipientKey: draftRecipientKey,
                            sendDisabled: !preview.canSend,
                            onSend: async ({
                                content,
                                englishSource,
                                attachmentIds,
                            }) => {
                                if (recipient === null) {
                                    toast.error('Choose who to text first', {
                                        persistNotification: false,
                                    });
                                    return null;
                                }
                                try {
                                    const conversationId =
                                        await createConversationMessage({
                                            organizationId,
                                            recipient,
                                            content,
                                            englishSource,
                                            attachmentIds,
                                        });
                                    toast.success('Message sent', {
                                        persistNotification: false,
                                    });
                                    await onMessageCreated(conversationId);
                                    return conversationId;
                                } catch (error) {
                                    console.error(
                                        'Failed to send conversation message',
                                        error,
                                    );
                                    toast.error(
                                        error instanceof ApiError &&
                                            error.message.trim() !== ''
                                            ? error.message
                                            : 'Failed to send message',
                                        { persistNotification: false },
                                    );
                                    return null;
                                }
                            },
                        }}
                        onBusyChange={handleBusyChange}
                        organizationId={organizationId}
                        timezone={timezone}
                    />
                </div>
            </div>
        </div>
    );
}
